import { fetchLibraryManagedProductGroupsFeaturedPages } from './apiFetchLibraryManagedProductGroupsFeaturedPages';
import { useQuery } from '@tanstack/react-query';
import { GlobalReactQueryOptions } from '../../../../config';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../store/appState';
import AuthorizationUtils from '../../../../shared/utils/utils-authorization';
import { useEnvironment } from '../../../../shared/components/EnvironmentContext';
import { ENVIRONMENT_DEMO } from '../../../../constants';
import {
    LibraryManagedProductGroupOnSiteOut,
    LoanFormatEnum,
    ScopeWithLibraryManagedProductGroupCountsOut
} from '../../../../store/api-catman';
import { FeaturedPageOut } from '../../../../store/api';
import { useMemo } from 'react';
import { browserClient } from '../../../../react-query';

const LIBRARY_MANAGED_PRODUCT_GROUPS_FEATURED_PAGES_QUERY = 'libraryManagedProductGroupsFeaturedPages';

export const useLibraryManagedProductGroupsFeaturePages = () => {
    const environment = useEnvironment();
    const isInPartnerContext = useSelector((state: AppState) =>
        AuthorizationUtils.isInPartnerContext(state.authentication)
    );

    return useQuery(
        [LIBRARY_MANAGED_PRODUCT_GROUPS_FEATURED_PAGES_QUERY],
        fetchLibraryManagedProductGroupsFeaturedPages,
        {
            ...GlobalReactQueryOptions,
            enabled: !isInPartnerContext && environment !== ENVIRONMENT_DEMO
        }
    );
};

export const useLibraryManagedScopes = () => {
    const { data } = useLibraryManagedProductGroupsFeaturePages();
    return useMemo(() => (data ? mapScopes(data) : []), [data]);
};

export const useLibraryManagedProductGroups = () => {
    const { data } = useLibraryManagedProductGroupsFeaturePages();
    return useMemo(() => (data ? mapProductGroups(data) : []), [data]);
};

const mapScopes = (data: FeaturedPageOut[]): ScopeWithLibraryManagedProductGroupCountsOut[] => {
    return data.map(scope => {
        return {
            activeProductGroupCount: scope.productGroupSlots?.filter(slot => slot.active).length,
            label: scope.label,
            loanFormat: scope.loanFormat as LoanFormatEnum | null,
            scopeId: scope.scopeId,
            totalProductGroupCount: scope.productGroupSlots?.length
        };
    });
};

const mapProductGroups = (data: FeaturedPageOut[]): LibraryManagedProductGroupOnSiteOut[] => {
    return data.flatMap(scope => {
        if (!scope.productGroupSlots) return [];

        return scope.productGroupSlots.map(slot => {
            return {
                productGroupId: slot.productGroup?.id!,
                productGroupName: slot.productGroup?.title,
                scope: scope.label,
                scopeId: scope.scopeId,
                scopeLabel: scope.label,
                scopeLoanFormat: scope.loanFormat as LoanFormatEnum | null
            };
        });
    });
};

export const invalidateLibraryManagedProductGroupFeaturedPages = () => {
    return browserClient.invalidateQueries([LIBRARY_MANAGED_PRODUCT_GROUPS_FEATURED_PAGES_QUERY]);
};
